import { Component, type ReactNode, type ErrorInfo } from 'react'
import { IconAlertTriangle, IconRefresh } from '@tabler/icons-react'
import { analytics } from '../../lib/analytics'

type Props = { children: ReactNode; fallback?: ReactNode }
type State = { hasError: boolean; error: Error | null }

export class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props)
    this.state = { hasError: false, error: null }
  }

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error }
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    console.error('[ErrorBoundary]', error, info)
    analytics.captureException(error, { componentStack: info.componentStack })
  }

  handleReset = () => this.setState({ hasError: false, error: null })

  render() {
    if (this.state.hasError) {
      if (this.props.fallback) return this.props.fallback
      return (
        <div className="flex flex-col items-center justify-center h-full min-h-64 text-center px-6">
          <div className="w-12 h-12 rounded-xl bg-red-950/50 border border-red-900/50 flex items-center justify-center mb-4">
            <IconAlertTriangle size={20} className="text-red-400" />
          </div>
          <h2 className="text-sm font-medium text-zinc-300 mb-1">Something went wrong</h2>
          <p className="text-xs text-zinc-600 max-w-xs mb-4 leading-relaxed">
            {this.state.error?.message ?? 'An unexpected error occurred'}
          </p>
          <button onClick={this.handleReset}
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs border border-border-default text-zinc-400 hover:text-zinc-200 hover:bg-surface-raised transition-colors">
            <IconRefresh size={13} /> Try again
          </button>
        </div>
      )
    }
    return this.props.children
  }
}

export function InlineError({ message, onRetry }: { message: string; onRetry?: () => void }) {
  return (
    <div className="flex items-center gap-2 px-3 py-2 rounded-lg bg-red-950/30 border border-red-900/30 text-xs text-red-400">
      <IconAlertTriangle size={13} className="shrink-0" />
      <span className="flex-1">{message}</span>
      {onRetry && (
        <button onClick={onRetry} className="text-red-400 hover:text-red-300 underline shrink-0">Retry</button>
      )}
    </div>
  )
}
